import React from 'react';
import {DataGrid} from "@mui/x-data-grid";
import {frFR} from '@mui/x-data-grid/locales';
import {Chip, Stack, Tooltip, Typography} from "@mui/material";
import ReplayIcon from '@mui/icons-material/Replay';
import {helper} from "../../../utils/helper";
import Constants from "../../../utils/constants";
import {describePrecedente, isRecidive} from "./recidiveRules.js";

/**
 * Chip visualisant l'écart : "2 lavés / 1 attendu".
 * Rouge si l'écart est important (probable erreur de saisie),
 * orange sinon (probable évolution du patrimoine).
 */
export const EcartChip = ({nombreLaves, quantitePatrimoine}) => {
    const attendu = quantitePatrimoine ?? 0;
    const laves = nombreLaves ?? 0;
    const isGrosEcart = attendu > 0 ? (laves / attendu >= 3 || laves === 0) : laves >= 3;

    return (
        <Chip
            size="small"
            label={`${laves} lavé${laves > 1 ? 's' : ''} / ${attendu} attendu${attendu > 1 ? 's' : ''}`}
            sx={{
                fontWeight: 'bold',
                backgroundColor: isGrosEcart ? '#B22222' : '#e67e22',
                color: 'white'
            }}
        />
    );
};

/**
 * Chip "lavage impossible" : affiche la raison réelle (libellé du problème
 * rattaché, ex. "Conteneur non collecté"). Fallback générique si aucun problème.
 * S'il y a plusieurs problèmes : le premier + compteur, liste complète en tooltip.
 */
export const LavageImpossibleChip = ({problemes}) => {
    const libelles = (problemes ?? []).map(p => p.libelle).filter(Boolean);

    const label = libelles.length > 0
        ? libelles[0] + (libelles.length > 1 ? ` +${libelles.length - 1}` : '')
        : 'Lavage impossible — sans raison';

    const tooltip = libelles.length > 0
        ? `Lavage impossible : ${libelles.join(', ')}`
        : 'Lavage impossible, aucun problème rattaché';

    return (
        <Tooltip title={tooltip}>
            <Chip
                size="small"
                label={label}
                sx={{fontWeight: 'bold', backgroundColor: '#B22222', color: 'white'}}
            />
        </Tooltip>
    );
};

/**
 * Chip « RÉCIDIVE » (CQ-01) : le passage précédent du conteneur était déjà impossible.
 * Calculé par l'API (anomalie.recidive), le tooltip donne la date et les raisons du précédent.
 * Une info de plus sur le lavage impossible à vérifier — pas un type, pas une action.
 */
export const RecidiveChip = ({anomalie}) => {
    if (!isRecidive(anomalie)) return null;
    return (
        <Tooltip title={describePrecedente(anomalie)}>
            <Chip
                size="small"
                icon={<ReplayIcon sx={{fontSize: 16, color: 'white !important'}}/>}
                label="RÉCIDIVE"
                sx={{fontWeight: 'bold', backgroundColor: '#5b1a1a', color: 'white', letterSpacing: 0.5}}
            />
        </Tooltip>
    );
};

/** Rendu de la colonne "Anomalie" selon le type de la ligne (+ tag récidive sur un lavage impossible) */
export const AnomalieChip = ({anomalie}) => {
    if (anomalie.type === Constants.ANOMALIE_TYPE_LAVAGE_IMPOSSIBLE) {
        return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{height: '100%'}}>
                <LavageImpossibleChip problemes={anomalie.intervention?.problemes}/>
                <RecidiveChip anomalie={anomalie}/>
            </Stack>
        );
    }

    return (
        <EcartChip
            nombreLaves={anomalie.nombreLaves}
            quantitePatrimoine={anomalie.quantitePatrimoine}
        />
    );
};

function AnomaliesTable({anomalies, onRowClick}) {
    const columns = [
        {
            field: 'date',
            headerName: 'Date',
            width: 150,
            valueGetter: (value, row) => row.intervention?.dateEvenement,
            renderCell: (params) => helper.getFormattedDatetime(params.value),
        },
        {
            field: 'patrimoine',
            headerName: 'Patrimoine',
            flex: 1,
            minWidth: 160,
            valueGetter: (value, row) => row.intervention?.conteneur?.patrimoine?.libelle ?? '—',
        },
        {
            field: 'conteneur',
            headerName: 'Conteneur',
            flex: 1,
            minWidth: 140,
            valueGetter: (value, row) => row.intervention?.conteneur?.nomenclature ?? '—',
            renderCell: (params) => (
                <Typography variant="body2" sx={{fontWeight: 600, lineHeight: '52px'}}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'adresse',
            headerName: 'Adresse',
            flex: 1.5,
            minWidth: 200,
            valueGetter: (value, row) => {
                const c = row.intervention?.conteneur;
                if (!c) return '—';
                return [c.adresse, c.commune].filter(Boolean).join(', ');
            },
        },
        {
            field: 'anomalie',
            headerName: 'Anomalie',
            width: 300,
            sortable: false,
            renderCell: (params) => <AnomalieChip anomalie={params.row}/>,
        },
        {
            field: 'operateur',
            headerName: 'Opérateur',
            width: 140,
            valueGetter: (value, row) => row.intervention?.operateur?.libelle ?? '—',
        },
        {
            field: 'photos',
            headerName: 'Photos',
            width: 90,
            sortable: false,
            valueGetter: (value, row) => row.intervention?.photos?.length ?? 0,
            renderCell: (params) => `${params.value} 📷`,
        },
    ];

    return (
        <DataGrid
            rows={anomalies || []}
            columns={columns}
            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
            onRowClick={(params) => onRowClick(params.row)}
            initialState={{
                sorting: {sortModel: [{field: 'date', sort: 'desc'}]},
                pagination: {paginationModel: {pageSize: 25}},
            }}
            pageSizeOptions={[25, 50, 100]}
            autoHeight
            sx={{
                '& .MuiDataGrid-row': {cursor: 'pointer'},
                backgroundColor: 'white',
            }}
        />
    );
}

export default AnomaliesTable;
